class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!el || !html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner == null) return;
      el.innerHTML = inner;
    });

    this.refreshWholesaleCheckout(parsedState).finally(() => {
      if (this.header) this.header.reveal();
      this.open();
    });
  }

  refreshWholesaleCheckout(parsedState) {
    const cartUrl = window.routes?.cart_url || '/cart';
    return fetch(`${cartUrl}.js`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('cart');
        return response.json();
      })
      .then((cart) => this.updateWholesaleCheckout(cart))
      .catch(() => this.updateWholesaleCheckout(parsedState));
  }

  updateWholesaleCheckout(cart) {
    const root = document.getElementById('cart-notification-checkout');
    if (!root || !cart) return;

    const qtyEnabled = root.dataset.qtyEnabled === 'true';
    const priceEnabled = root.dataset.priceEnabled === 'true';
    const minQty = Number(root.dataset.minQty || 10);
    const minPrice = Number(root.dataset.minPrice || 0);
    this.cartCurrency = cart.currency || this.cartCurrency || 'USD';

    let wholesaleQty = 0;
    let wholesaleTotal = 0;
    (cart.items || []).forEach((item) => {
      if (!this.isWholesaleCartItem(item)) return;
      wholesaleQty += Number(item.quantity) || 0;
      wholesaleTotal += Number(item.final_line_price ?? item.line_price ?? 0);
    });

    const qtyBlocked = qtyEnabled && wholesaleQty > 0 && wholesaleQty < minQty;
    const priceBlocked = priceEnabled && minPrice > 0 && wholesaleTotal > 0 && wholesaleTotal < minPrice;
    const blocked = qtyBlocked || priceBlocked;
    const button = root.querySelector('[name="checkout"]');
    if (button) button.disabled = !cart.item_count || blocked;

    const noticeId = 'WholesaleMinNotice';
    let notice = root.querySelector(`#${noticeId}`);
    if (!blocked) {
      notice?.remove();
      return;
    }

    const messages = [];
    if (qtyBlocked) {
      const remaining = Math.max(minQty - wholesaleQty, 0);
      messages.push(
        `Wholesale orders need at least ${minQty} units. Your cart has ${wholesaleQty}. Add ${remaining} more.`
      );
    }
    if (priceBlocked) {
      const remaining = Math.max(minPrice - wholesaleTotal, 0);
      messages.push(
        `Wholesale orders need at least ${this.formatMoney(minPrice)} in wholesale products. Your cart has ${this.formatMoney(wholesaleTotal)}. Add ${this.formatMoney(remaining)} more.`
      );
    }

    if (!notice) {
      notice = document.createElement('div');
      notice.id = noticeId;
      notice.className = 'wholesale-min-notice';
      notice.setAttribute('role', 'status');
      const form = root.querySelector('form');
      root.insertBefore(notice, form);
    }
    notice.innerHTML = messages.map((message) => `<p>${message}</p>`).join('');
  }

  isWholesaleCartItem(item) {
    const tags = item.tags || item.product_tags || [];
    if (tags.some((tag) => String(tag).toLowerCase().trim() === 'wholesale-product')) return true;
    const haystack = `${item.product_title || ''} ${item.title || ''} ${item.handle || ''}`.toLowerCase();
    return haystack.includes('wholesale');
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.cartCurrency || 'USD',
    }).format(Number(cents || 0) / 100);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
